import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CreditCard, Landmark, Lock, MapPin, Pencil, ShieldCheck, Trash2, Truck, User } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useStore } from "../../context/StoreContext";
import { fmtTRY } from "../../lib/format";
import { TURKEY_CITIES, TURKEY_DISTRICTS } from "../../constants/turkey";

const EMPTY_ADDRESS = { title: "", full_name: "", phone: "", city: "", district: "", neighborhood: "", address_line: "", postal_code: "" };

const addrInputCls = "w-full h-12 px-4 bg-white border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600";

// Ödeme sayfası kargo/teslimat tercihleri (backend ile aynı liste — routers/orders.py)
const CARGO_CARRIERS = ["Farketmez (en hızlı)", "Aras Kargo", "Yurtiçi Kargo", "MNG Kargo", "Sürat Kargo", "PTT Kargo", "Hepsijet"];
const DELIVERY_NOTES = ["Standart teslimat", "Kapıya bırakılabilir", "Komşuya teslim edilebilir",
  "Güvenlik/danışmaya teslim edilebilir", "Teslimattan önce aransın", "Kargo şubesinden teslim alacağım"];

// ÖNEMLİ: Bu bileşen modül kapsamında tanımlı — CheckoutPage'in İÇİNDE tanımlanırsa
// her tuş vuruşunda yeniden oluşturulur, input'un odağı kaybolur ve kullanıcı tek
// harften fazlasını yazamaz (önceki canlı hatanın kök nedeni buydu).
function AddressFields({ value, onChange, prefix }) {
  const districts = TURKEY_DISTRICTS[value.city] || [];
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <label className="text-[11px] font-semibold text-neutral-500 block mb-1.5">Ad Soyad *</label>
        <input required value={value.full_name} onChange={(e) => onChange("full_name", e.target.value)} placeholder="Ad Soyad"
          className={addrInputCls} data-testid={`${prefix}-fullname`} autoComplete="name" />
      </div>
      <div>
        <label className="text-[11px] font-semibold text-neutral-500 block mb-1.5">Telefon *</label>
        <input required value={value.phone} onChange={(e) => onChange("phone", e.target.value)} placeholder="05xx xxx xx xx"
          className={addrInputCls} data-testid={`${prefix}-tel`} autoComplete="tel" inputMode="tel" />
      </div>
      <div>
        <label className="text-[11px] font-semibold text-neutral-500 block mb-1.5">Şehir *</label>
        <select required value={value.city} onChange={(e) => { onChange("city", e.target.value); onChange("district", ""); }}
          className={`${addrInputCls} ${!value.city ? "text-neutral-400" : ""}`} data-testid={`${prefix}-city`}>
          <option value="">Şehir seçin</option>
          {TURKEY_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[11px] font-semibold text-neutral-500 block mb-1.5">İlçe *</label>
        <select required value={value.district} disabled={!value.city}
          onChange={(e) => onChange("district", e.target.value)}
          className={`${addrInputCls} disabled:opacity-50 disabled:cursor-not-allowed ${!value.district ? "text-neutral-400" : ""}`}
          data-testid={`${prefix}-district`}>
          <option value="">{value.city ? "İlçe seçin" : "Önce şehir seçin"}</option>
          {districts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[11px] font-semibold text-neutral-500 block mb-1.5">Mahalle</label>
        <input value={value.neighborhood || ""} onChange={(e) => onChange("neighborhood", e.target.value)} placeholder="Örn: Atatürk Mahallesi"
          className={addrInputCls} data-testid={`${prefix}-neighborhood`} />
      </div>
      <div>
        <label className="text-[11px] font-semibold text-neutral-500 block mb-1.5">Posta Kodu</label>
        <input value={value.postal_code || ""} onChange={(e) => onChange("postal_code", e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="34xxx"
          className={addrInputCls} data-testid={`${prefix}-postal`} inputMode="numeric" />
      </div>
      <div className="sm:col-span-2">
        <label className="text-[11px] font-semibold text-neutral-500 block mb-1.5">Adres *</label>
        <textarea required value={value.address_line} onChange={(e) => onChange("address_line", e.target.value)}
          placeholder={"Sokak, cadde, bina ve daire bilgisi\nÖrn: 1234. Sokak No:15 Daire:8"} rows={3}
          className="w-full px-4 py-3 bg-white border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600 resize-y"
          data-testid={`${prefix}-address`} />
      </div>
      <div className="sm:col-span-2">
        <label className="text-[11px] font-semibold text-neutral-500 block mb-1.5">Adres Başlığı</label>
        <select value={value.title || ""} onChange={(e) => onChange("title", e.target.value)}
          className={`${addrInputCls} ${!value.title ? "text-neutral-400" : ""}`} data-testid={`${prefix}-title`}>
          <option value="">Seçin (opsiyonel)</option>
          <option value="Ev">Ev</option>
          <option value="İş">İş</option>
          <option value="Diğer">Diğer</option>
        </select>
      </div>
    </div>
  );
}

export default function CheckoutPage() {
  const { cart, customer, refreshCart } = useStore();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [contact, setContact] = useState({ email: "", name: "", phone: "" });
  const [shipping, setShipping] = useState(EMPTY_ADDRESS);
  const [billing, setBilling] = useState(EMPTY_ADDRESS);
  const [sameBilling, setSameBilling] = useState(true);
  const [note, setNote] = useState("");
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [saveAddress, setSaveAddress] = useState(false);
  const [shippingPref, setShippingPref] = useState({ carrier: CARGO_CARRIERS[0], delivery_note: DELIVERY_NOTES[0] });
  const [invoiceType, setInvoiceType] = useState("bireysel");
  const [invoiceForm, setInvoiceForm] = useState({ tckn: "", vkn: "", tax_office: "", company_name: "" });
  const [kvkk, setKvkk] = useState(false);
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("credit_card");
  const [cardEnabled, setCardEnabled] = useState(true);
  const [providerCode, setProviderCode] = useState("garanti");
  const [providerLabel, setProviderLabel] = useState("");
  const [isTestMode, setIsTestMode] = useState(false);
  const { settings } = useStore();

  useEffect(() => {
    // Kart ödemesi yalnızca backend izin veriyorsa gösterilir (LIVE + credential yoksa gizli)
    api.get("/payments/config").then(({ data }) => {
      setCardEnabled(Boolean(data.card_enabled));
      setProviderCode((data.provider || "garanti").split("-")[0]);
      setProviderLabel(data.provider_label || "");
      setIsTestMode(Boolean(data.is_test));
      if (!data.card_enabled) setPaymentMethod("havale");
    }).catch(() => {});
  }, []);

  // iyzico/PayTR gibi hosted sağlayıcılarda kart, sağlayıcının güvenli sayfasında girilir
  const hostedProvider = ["iyzico", "paytr"].includes(providerCode);

  useEffect(() => {
    if (customer) {
      setContact({ email: customer.email, name: customer.name, phone: customer.phone || "" });
      api.get("/addresses").then(({ data }) => {
        setAddresses(data);
        const def = data.find((a) => a.is_default) || data[0];
        if (def) { setSelectedAddressId(def.address_id); setShipping(def); }
      }).catch(() => {});
    }
  }, [customer]);

  useEffect(() => {
    if (cart && cart.lines?.length === 0 && !order) navigate("/sepet");
  }, [cart, order, navigate]);

  const createOrder = async (e) => {
    e.preventDefault();
    if (!kvkk) { toast.error("Devam etmek için Mesafeli Satış Sözleşmesi'ni onaylayın"); return; }
    setBusy(true);
    try {
      // Adres kaydet/güncelle (üye ise ve kutu işaretliyse) — siparişten bağımsız, hata siparişi engellemez
      if (customer && saveAddress) {
        try {
          const payload = { ...shipping, is_default: false };
          if (editingAddressId) {
            await api.put(`/addresses/${editingAddressId}`, payload);
          } else {
            await api.post("/addresses", payload);
          }
          const { data: fresh } = await api.get("/addresses");
          setAddresses(fresh);
        } catch { toast.warning("Adres kaydedilemedi ancak siparişiniz oluşturuluyor"); }
      }
      const { data } = await api.post("/checkout", {
        contact, shipping_address: shipping,
        billing_address: sameBilling ? null : billing,
        same_billing: sameBilling,
        invoice_info: {
          customer_type: invoiceType,
          tckn: invoiceType === "bireysel" ? invoiceForm.tckn : null,
          vkn: invoiceType === "kurumsal" ? invoiceForm.vkn : null,
          tax_office: invoiceType === "kurumsal" ? invoiceForm.tax_office : null,
          company_name: invoiceType === "kurumsal" ? invoiceForm.company_name : null,
        },
        note, shipping_preference: shippingPref, payment_method: paymentMethod,
      });
      setOrder(data);
      refreshCart();
      window.scrollTo({ top: 0 });
    } catch (err) {
      const details = err?.response?.data?.detail;
      if (err?.response?.status === 422 && Array.isArray(details)) {
        // Pydantic doğrulama hatalarını Türkçe ve anlaşılır göster
        const msgs = details.map((d) => {
          const field = d?.loc?.[d.loc.length - 1];
          if (d?.type === "value_error" || d?.type?.includes("email")) {
            if (field === "email") return "Geçerli bir e-posta adresi girin";
          }
          if (field === "email") return "E-posta adresi geçersiz";
          if (field === "full_name" || field === "name") return "Ad soyad eksik";
          if (field === "phone") return "Telefon numarası eksik veya geçersiz";
          return `${field || "Alan"}: ${d?.msg || "geçersiz"}`;
        });
        toast.error(msgs.join(" · "));
      } else {
        toast.error(errMsg(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const [installment, setInstallment] = useState(1);
  const [installments, setInstallments] = useState([]);
  const [card, setCard] = useState({ name: "", number: "", exp: "", cvv: "" });

  useEffect(() => {
    if (order) {
      api.get(`/payments/installments?amount=${order.total}`).then(({ data }) => setInstallments(data.options || [])).catch(() => {});
    }
  }, [order]);

  const formatCard = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(\d{4})(?=\d)/g, "$1 ");
  const formatExp = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  };
  const cardValid = card.name.trim().length >= 3 && card.number.replace(/\s/g, "").length === 16 && card.exp.length === 5 && card.cvv.length >= 3;

  const startPayment = async () => {
    if (!hostedProvider && !cardValid) { toast.error("Kart bilgilerini kontrol edin"); return; }
    setPaying(true);
    try {
      // Kart bilgileri backend'e GÖNDERİLMEZ — 3D formu doğrudan bankaya gider.
      const { data } = await api.post(`/orders/${order.order_number}/payment/start`, { installment });
      if (data.mode === "3ds" && data.action) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.action;
        Object.entries(data.fields || {}).forEach(([k, v]) => {
          const input = document.createElement("input");
          input.type = "hidden"; input.name = k; input.value = v;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        return;
      }
      if (data.mode === "redirect" && data.redirect_url) {
        window.location.href = data.redirect_url;  // iyzico hosted ödeme sayfası
        return;
      }
      navigate(data.redirect_url);  // mock3d ve paytr iframe sayfası
    } catch (err) {
      toast.error(errMsg(err));
      setPaying(false);
    }
  };

  if (order && order.payment_method === "havale") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-14">
        <Helmet><title>Sipariş Alındı | BIGELDİ</title></Helmet>
        <div className="bg-white rounded-3xl border border-[#E6E4DD] p-8" data-testid="havale-panel">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-full bg-amber-600/10 text-amber-700 flex items-center justify-center"><Landmark size={20} /></div>
            <div>
              <h1 className="font-display font-extrabold text-2xl">Siparişiniz Alındı</h1>
              <p className="text-xs text-neutral-400 font-mono-ed">Sipariş: {order.order_number}</p>
            </div>
          </div>
          <div className="flex justify-between items-center my-6 py-4 border-y border-[#E6E4DD]">
            <span className="text-sm text-neutral-500">Havale/EFT Tutarı</span>
            <span className="font-mono-ed font-bold text-3xl" data-testid="havale-amount">{fmtTRY(order.total)}</span>
          </div>
          <div className="bg-[#FAF9F5] border border-[#E6E4DD] rounded-2xl p-5 space-y-2 text-sm" data-testid="havale-bank-info">
            <p className="text-xs uppercase tracking-widest text-neutral-400 font-mono-ed mb-2">Banka Bilgileri</p>
            <p><span className="text-neutral-500">Banka:</span> <b>{settings?.bank_name || "—"}</b></p>
            <p><span className="text-neutral-500">Hesap Sahibi:</span> <b>{settings?.bank_account_holder || "—"}</b></p>
            <p><span className="text-neutral-500">IBAN:</span> <b className="font-mono-ed" data-testid="havale-iban">{settings?.bank_iban || "—"}</b></p>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">{settings?.havale_note}</p>
          </div>
          <p className="text-xs text-neutral-500 mt-4">Ödemeniz onaylandığında e-posta ile bilgilendirileceksiniz. Sipariş durumunuzu <Link to={`/siparis-takip`} className="text-amber-700 font-semibold underline">sipariş takibi</Link> sayfasından izleyebilirsiniz.</p>
          <Link to="/" className="mt-6 w-full h-13 py-4 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center" data-testid="havale-home-btn">
            Alışverişe Devam Et
          </Link>
        </div>
      </div>
    );
  }

  if (order) {
    const sel = installments.find((o) => o.count === installment);
    return (
      <div className="max-w-2xl mx-auto px-4 py-14">
        <Helmet><title>Ödeme | BIGELDİ</title></Helmet>
        <div className="bg-white rounded-3xl border border-[#E6E4DD] p-8" data-testid="mock-payment-panel">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-full bg-amber-600/10 text-amber-700 flex items-center justify-center"><CreditCard size={20} /></div>
            <div>
              <h1 className="font-display font-extrabold text-2xl">Güvenli Ödeme</h1>
              <p className="text-xs text-neutral-400 font-mono-ed">Sipariş: {order.order_number}</p>
            </div>
          </div>
          {isTestMode ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 mt-4" data-testid="test-mode-notice">
              <b>TEST MODU:</b> {providerLabel || "Ödeme sağlayıcısı"} akışı simüle edilir; gerçek tahsilat yapılmaz.
              Kart bilgileriniz banka ekranında işlenir, sistemimizde saklanmaz.
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-xs text-emerald-800 mt-4 flex items-start gap-2" data-testid="live-mode-notice">
              <ShieldCheck size={15} className="text-emerald-600 mt-0.5 shrink-0" />
              <p><b>Güvenli Ödeme:</b> {providerLabel || "Banka"} 3D Secure güvenli ödeme sayfasına yönlendirileceksiniz. Kart bilgileriniz yalnızca banka ekranında işlenir, sistemimizde saklanmaz.</p>
            </div>
          )}
          <div className="flex justify-between items-center my-6 py-4 border-y border-[#E6E4DD]">
            <span className="text-sm text-neutral-500">Ödenecek Tutar</span>
            <span className="font-mono-ed font-bold text-3xl" data-testid="payment-amount">{fmtTRY(sel?.total ?? order.total)}</span>
          </div>

          <div className="space-y-3">
            {hostedProvider ? (
              <div className="bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl p-5 text-sm text-neutral-600 flex items-start gap-3" data-testid="hosted-provider-note">
                <Lock size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                <p>Kart bilgilerinizi <b>{providerLabel || providerCode}</b> güvenli ödeme sayfasında gireceksiniz. Kart verileri hiçbir zaman Bigeldi sunucusuna ulaşmaz ve saklanmaz.</p>
              </div>
            ) : (
            <>
            <input value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} placeholder="Kart Üzerindeki Ad Soyad"
              className="w-full h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600" data-testid="card-holder" autoComplete="cc-name" />
            <div className="relative">
              <input value={card.number} onChange={(e) => setCard({ ...card, number: formatCard(e.target.value) })} placeholder="Kart Numarası" inputMode="numeric"
                className="w-full h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm font-mono-ed tracking-widest focus:outline-none focus:border-amber-600" data-testid="card-number" autoComplete="cc-number" />
              <Lock size={15} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-300" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={card.exp} onChange={(e) => setCard({ ...card, exp: formatExp(e.target.value) })} placeholder="AA/YY" inputMode="numeric"
                className="h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm font-mono-ed focus:outline-none focus:border-amber-600" data-testid="card-expiry" autoComplete="cc-exp" />
              <input value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="CVV" inputMode="numeric" type="password"
                className="h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm font-mono-ed focus:outline-none focus:border-amber-600" data-testid="card-cvv" autoComplete="cc-csc" />
            </div>
            </>
            )}
            {installments.length > 1 && (
              <div>
                <p className="text-[11px] uppercase tracking-widest text-neutral-400 font-mono-ed mb-2 mt-1">Taksit Seçimi</p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="installment-options">
                  {installments.map((o) => (
                    <button type="button" key={o.count} onClick={() => setInstallment(o.count)}
                      className={`rounded-xl border-2 px-2 py-2.5 text-center transition-colors ${installment === o.count ? "border-amber-600 bg-amber-50" : "border-[#E6E4DD] hover:border-amber-400"}`}
                      data-testid={`installment-${o.count}`}>
                      <span className="block text-sm font-bold">{o.count === 1 ? "Peşin" : `${o.count} taksit`}</span>
                      <span className="block text-[10px] text-neutral-500 font-mono-ed">{o.count === 1 ? fmtTRY(o.total) : `${fmtTRY(o.monthly)}/ay`}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button onClick={startPayment} disabled={paying || (!hostedProvider && !cardValid)}
            className="w-full h-13 py-4 mt-6 rounded-full bg-[#121214] text-white font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="complete-payment-btn">
            <Lock size={16} /> {paying ? "Ödeme sayfasına yönlendiriliyor..." : hostedProvider ? "Güvenli Ödeme Sayfasına Git" : "3D Secure ile Öde"}
          </button>
          <p className="text-[11px] text-neutral-400 text-center mt-4">Kart bilgileri hiçbir zaman saklanmaz ve loglanmaz. Ödeme {providerLabel || "Garanti BBVA"} güvencesiyle işlenir.</p>
        </div>
      </div>
    );
  }

  const setField = (setter) => (name, value) => setter((s) => ({ ...s, [name]: value }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Helmet><title>Ödeme | BIGELDİ</title></Helmet>
      <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight mb-2" data-testid="checkout-title">Ödeme & Teslimat</h1>
      <div className="flex items-center gap-1.5 sm:gap-3 mb-8 text-[11px] sm:text-xs font-semibold" data-testid="checkout-steps">
        {[["1", "Sepet", true], ["2", "Teslimat & Fatura", true], ["3", "Ödeme", false], ["4", "Sonuç", false]].map(([n, label, done], i, arr) => (
          <div key={n} className="flex items-center gap-1.5 sm:gap-3">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${done ? "bg-emerald-600 text-white" : "bg-white border border-[#E6E4DD] text-neutral-400"}`}>{n}</span>
            <span className={done ? "text-emerald-700" : "text-neutral-400"}>{label}</span>
            {i < arr.length - 1 && <span className="w-4 sm:w-8 h-px bg-[#E6E4DD]" />}
          </div>
        ))}
      </div>

      <form onSubmit={createOrder} className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white rounded-2xl border border-[#E6E4DD] p-6">
            <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><User size={18} className="text-amber-700" /> İletişim Bilgileri</h2>
            {!customer && (
              <p className="text-xs text-neutral-500 mb-4 bg-[#FAF9F5] rounded-lg p-3">
                Misafir olarak alışveriş yapıyorsunuz. <Link to="/giris" className="text-amber-700 font-semibold underline" data-testid="checkout-login-link">Giriş yapın</Link> veya üye olun.
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <input name="email" type="email" required value={contact.email} onChange={(e) => setContact((s) => ({ ...s, email: e.target.value }))} placeholder="E-posta *"
                className="h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600" data-testid="checkout-form-email" />
              <input name="name" required value={contact.name} onChange={(e) => setContact((s) => ({ ...s, name: e.target.value }))} placeholder="Ad Soyad *"
                className="h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600" data-testid="checkout-form-name" />
              <input name="phone" required value={contact.phone} onChange={(e) => setContact((s) => ({ ...s, phone: e.target.value }))} placeholder="Telefon *"
                className="h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600 sm:col-span-2" data-testid="checkout-form-phone" />
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-[#E6E4DD] p-6">
            <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><MapPin size={18} className="text-amber-700" /> Teslimat Adresi</h2>
            {addresses.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-4">
                {addresses.map((a) => (
                  <div key={a.address_id}
                    className={`relative px-4 py-2.5 rounded-xl border-2 text-xs font-medium text-left transition-colors cursor-pointer ${selectedAddressId === a.address_id ? "border-amber-600 bg-amber-50" : "border-[#E6E4DD] hover:border-amber-400"}`}
                    onClick={() => { setSelectedAddressId(a.address_id); setEditingAddressId(null); setShipping({ ...EMPTY_ADDRESS, ...a }); }}
                    data-testid={`checkout-address-${a.address_id}`}>
                    <span className="block font-bold">{a.title || a.full_name}</span>
                    <span className="text-neutral-500">{a.city} / {a.district}</span>
                    <span className="absolute -top-2 -right-2 flex gap-1">
                      <button type="button" title="Düzenle"
                        onClick={(e) => { e.stopPropagation(); setSelectedAddressId(a.address_id); setEditingAddressId(a.address_id); setShipping({ ...EMPTY_ADDRESS, ...a }); }}
                        className="h-5 w-5 rounded-full bg-white border border-[#E6E4DD] text-neutral-500 hover:text-amber-700 hover:border-amber-500 flex items-center justify-center"
                        data-testid={`checkout-address-edit-${a.address_id}`}>
                        <Pencil size={10} />
                      </button>
                      <button type="button" title="Sil"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm("Bu adres silinsin mi?")) return;
                          try {
                            await api.delete(`/addresses/${a.address_id}`);
                            const next = addresses.filter((x) => x.address_id !== a.address_id);
                            setAddresses(next);
                            if (selectedAddressId === a.address_id) { setSelectedAddressId(null); setShipping({ ...EMPTY_ADDRESS, full_name: contact.name, phone: contact.phone }); }
                            toast.success("Adres silindi");
                          } catch (err) { toast.error(errMsg(err)); }
                        }}
                        className="h-5 w-5 rounded-full bg-white border border-[#E6E4DD] text-neutral-500 hover:text-rose-600 hover:border-rose-400 flex items-center justify-center"
                        data-testid={`checkout-address-delete-${a.address_id}`}>
                        <Trash2 size={10} />
                      </button>
                    </span>
                  </div>
                ))}
                <button type="button" onClick={() => { setSelectedAddressId(null); setEditingAddressId(null); setShipping({ ...EMPTY_ADDRESS, full_name: contact.name, phone: contact.phone }); }}
                  className={`px-4 py-2.5 rounded-xl border-2 border-dashed text-xs font-semibold transition-colors ${selectedAddressId === null && !editingAddressId ? "border-amber-600 bg-amber-50" : "border-[#E6E4DD]"}`}
                  data-testid="checkout-new-address">
                  + Yeni Adres Ekle
                </button>
              </div>
            )}
            <AddressFields value={shipping} onChange={setField(setShipping)} prefix="checkout-form" />
            {customer && (
              <label className="flex items-center gap-2.5 text-sm mt-4 cursor-pointer" data-testid="checkout-save-address-wrap">
                <input type="checkbox" checked={saveAddress} onChange={(e) => setSaveAddress(e.target.checked)}
                  className="accent-amber-600 w-4 h-4" data-testid="checkout-save-address" />
                {editingAddressId ? "Bu adresi güncelle" : "Bu adresi kayıtlı adreslerime ekle"}
              </label>
            )}
          </section>

          {/* Teslimat tercihleri: kargo firması + teslimat notu */}
          <section className="bg-white rounded-2xl border border-[#E6E4DD] p-6" data-testid="shipping-preference-section">
            <h2 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Truck size={18} className="text-amber-700" /> Teslimat Tercihleri</h2>
            <p className="text-xs text-neutral-500 mb-4">Kargo firması tercihinizi ve teslimat notunuzu seçin (opsiyonel).</p>
            <p className="text-[11px] font-semibold text-neutral-500 mb-2">Kargo Firması Tercihi</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {CARGO_CARRIERS.map((c) => (
                <button type="button" key={c} onClick={() => setShippingPref((s) => ({ ...s, carrier: c }))}
                  className={`px-3.5 py-2 rounded-full border text-xs font-medium transition-colors ${shippingPref.carrier === c ? "border-amber-600 bg-amber-50 text-amber-800" : "border-[#E6E4DD] text-neutral-600 hover:border-amber-400"}`}
                  data-testid={`checkout-carrier-${c.replace(/[^a-zA-ZğüşöçıİĞÜŞÖÇ0-9]/g, "-")}`}>
                  {c}
                </button>
              ))}
            </div>
            <p className="text-[11px] font-semibold text-neutral-500 mb-2">Teslimat Notu</p>
            <div className="flex flex-wrap gap-2">
              {DELIVERY_NOTES.map((n) => (
                <button type="button" key={n} onClick={() => setShippingPref((s) => ({ ...s, delivery_note: n }))}
                  className={`px-3.5 py-2 rounded-full border text-xs font-medium transition-colors ${shippingPref.delivery_note === n ? "border-amber-600 bg-amber-50 text-amber-800" : "border-[#E6E4DD] text-neutral-600 hover:border-amber-400"}`}
                  data-testid={`checkout-delivery-note-${DELIVERY_NOTES.indexOf(n)}`}>
                  {n}
                </button>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-[#E6E4DD] p-6">
            <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer mb-4">
              <input type="checkbox" checked={sameBilling} onChange={(e) => setSameBilling(e.target.checked)} className="accent-amber-600 w-4 h-4" data-testid="checkout-same-billing" />
              Fatura adresim teslimat adresimle aynı
            </label>
            {!sameBilling && <AddressFields value={billing} onChange={setField(setBilling)} prefix="billing-form" />}

            <div className="mt-5 pt-5 border-t border-[#E6E4DD]" data-testid="invoice-info-section">
              <p className="text-sm font-semibold mb-3">Fatura Tipi</p>
              <div className="flex gap-2 mb-4">
                {[["bireysel", "Bireysel"], ["kurumsal", "Kurumsal"]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setInvoiceType(v)}
                    className={`flex-1 h-11 rounded-xl border-2 text-sm font-semibold transition-colors ${invoiceType === v ? "border-amber-600 bg-amber-50 text-amber-800" : "border-[#E6E4DD]"}`}
                    data-testid={`invoice-type-${v}`}>
                    {l}
                  </button>
                ))}
              </div>
              {invoiceType === "bireysel" ? (
                <input value={invoiceForm.tckn} onChange={(e) => setInvoiceForm({ ...invoiceForm, tckn: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                  placeholder="T.C. Kimlik No (opsiyonel)" inputMode="numeric"
                  className="w-full h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm font-mono-ed focus:outline-none focus:border-amber-600" data-testid="invoice-tckn" />
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required value={invoiceForm.company_name} onChange={(e) => setInvoiceForm({ ...invoiceForm, company_name: e.target.value })} placeholder="Firma Unvanı *"
                    className="h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600" data-testid="invoice-company" />
                  <input required value={invoiceForm.tax_office} onChange={(e) => setInvoiceForm({ ...invoiceForm, tax_office: e.target.value })} placeholder="Vergi Dairesi *"
                    className="h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600" data-testid="invoice-tax-office" />
                  <input required value={invoiceForm.vkn} onChange={(e) => setInvoiceForm({ ...invoiceForm, vkn: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                    placeholder="Vergi Numarası *" inputMode="numeric"
                    className="h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm font-mono-ed focus:outline-none focus:border-amber-600 sm:col-span-2" data-testid="invoice-vkn" />
                </div>
              )}
              <p className="text-[10px] text-neutral-400 mt-2">e-Fatura mükellefiyeti QNB/GİB üzerinden doğrulanır; doğrulanamayan kurumsal faturalar e-Arşiv bekletilir.</p>
            </div>
            <textarea name="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Sipariş notu (opsiyonel)" rows={2}
              className="w-full mt-4 px-4 py-3 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600 resize-none" data-testid="checkout-note" />
          </section>
        </div>

        <aside className="bg-white rounded-2xl border border-[#E6E4DD] p-6 h-fit sticky top-36" data-testid="checkout-summary">
          <h2 className="font-display font-bold text-lg mb-4">Sipariş Özeti</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
            {cart?.lines?.map((l) => (
              <div key={`${l.product_id}-${l.variant_id || "b"}`} className="flex gap-3 text-sm">
                <div className="w-12 h-14 rounded-lg overflow-hidden bg-[#F4F2EB] shrink-0">
                  {l.image && <img src={l.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium line-clamp-1">{l.name}</p>
                  <p className="text-[11px] text-neutral-400">{l.variant_label ? `${l.variant_label} · ` : ""}{l.quantity} adet</p>
                </div>
                <p className="font-semibold">{fmtTRY(l.line_total)}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm border-t border-[#E6E4DD] pt-4">
            <div className="flex justify-between text-neutral-500"><span>Ara toplam</span><span>{fmtTRY(cart?.subtotal)}</span></div>
            {cart?.campaign_discount > 0 && <div className="flex justify-between text-emerald-700"><span>Kampanya</span><span>-{fmtTRY(cart.campaign_discount)}</span></div>}
            {cart?.coupon_discount > 0 && <div className="flex justify-between text-emerald-700"><span>Kupon ({cart.coupon_code})</span><span>-{fmtTRY(cart.coupon_discount)}</span></div>}
            <div className="flex justify-between text-neutral-500"><span>Kargo</span><span>{cart?.shipping_fee === 0 ? "Ücretsiz" : fmtTRY(cart?.shipping_fee)}</span></div>
            {cart?.shipping_fee > 0 && cart?.free_shipping_threshold > 0 && (
              <p className="text-[11px] text-amber-700" data-testid="free-shipping-note">{fmtTRY(cart.free_shipping_threshold)} ve üzeri alışverişlerde kargo ücretsiz.</p>
            )}
            <div className="flex justify-between font-mono-ed font-bold text-xl pt-2 border-t border-[#E6E4DD]"><span>Toplam</span><span data-testid="checkout-total">{fmtTRY(cart?.total)}</span></div>
          </div>
          <label className="flex items-start gap-2.5 text-[11px] text-neutral-500 mt-5 cursor-pointer leading-relaxed">
            <input type="checkbox" checked={kvkk} onChange={(e) => setKvkk(e.target.checked)} className="accent-amber-600 w-4 h-4 mt-0.5" data-testid="checkout-kvkk" />
            <span>Mesafeli Satış Sözleşmesi'ni ve KVKK Aydınlatma Metni'ni okudum, onaylıyorum.</span>
          </label>

          <div className="mt-5" data-testid="payment-method-section">
            <p className="text-xs font-semibold text-neutral-600 mb-2">Ödeme Yöntemi</p>
            <div className={`grid ${cardEnabled ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
              {cardEnabled && (
              <button type="button" onClick={() => setPaymentMethod("credit_card")}
                className={`h-12 rounded-xl border-2 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${paymentMethod === "credit_card" ? "border-amber-600 bg-amber-50 text-amber-800" : "border-[#E6E4DD] text-neutral-600 hover:border-amber-400"}`}
                data-testid="payment-method-card">
                <CreditCard size={15} /> Kredi Kartı
              </button>
              )}
              <button type="button" onClick={() => setPaymentMethod("havale")}
                className={`h-12 rounded-xl border-2 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${paymentMethod === "havale" ? "border-amber-600 bg-amber-50 text-amber-800" : "border-[#E6E4DD] text-neutral-600 hover:border-amber-400"}`}
                data-testid="payment-method-havale">
                <Landmark size={15} /> Havale / EFT
              </button>
            </div>
            {!cardEnabled && (
              <p className="text-[11px] text-neutral-600 bg-neutral-50 border border-[#E6E4DD] rounded-lg p-3 mt-2" data-testid="card-disabled-note">
                Kredi kartı ile ödeme şu anda aktif değil. Havale/EFT ile güvenle sipariş verebilirsiniz.
              </p>
            )}
            {paymentMethod === "havale" && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                Siparişiniz oluşturulduktan sonra banka bilgileri gösterilecektir. Ödemeniz onaylandıktan sonra siparişiniz işleme alınır.
              </p>
            )}
          </div>

          <button type="submit" disabled={busy}
            className="w-full h-13 py-4 mt-4 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
            data-testid="checkout-form-submit">
            {busy ? "Sipariş oluşturuluyor..." : paymentMethod === "havale" ? "Siparişi Oluştur (Havale/EFT)" : "Siparişi Oluştur"}
          </button>
          <p className="text-[10px] text-neutral-400 text-center mt-3 flex items-center justify-center gap-1"><Lock size={11} /> 256-bit SSL ile korunan ödeme</p>
        </aside>
      </form>
    </div>
  );
}
